/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useState, useContext, useEffect, useMemo, useRef } from "react";
import { Form, Formik, Field, FieldArray } from 'formik';
import { useHistory, useLocation } from "react-router-dom";
import { Label } from "components/Form";
import { Row, Col } from "components/Grid";
import CheckBox from "components/Form/CheckBox";
import { FitinsurAPI, MessageCenter } from "utils";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import Colors from "constants/Colors";
import {
  ButtonContent,
  ButtonContainer,
  Button,
  Dialog,
  CalendarContainer,
  Text,
  BoxFolder,
  InlineLabel,
  Line
} from "./styles";
import _ from "lodash";
import {DayPicker} from 'react-day-picker';
import { InputField } from "components/Form";
import Icon from "components/Icon";
import { faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Yup from 'yup';


export default function ShareDocuments({idOperation, show, changeState, screenState, changeShareDocumentsState, changeInitialMessage}) {
  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const location = useLocation();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const [showCalendarExpiration, setShowCalendarExpiration] = useState(false);
  const [supportDocuments, setSupportDocuments] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [hasHash, setHasHash] = useState(false);
  const [currentHash, setCurrentHash] = useState("");

  const [initial, setInitial] = useState({
    expirationDate: '',
  });

  const [sortBy, setSortBy] = useState(
    {
      key: null,
      direction: null,
    }
  );

  const columns = [
    {
      title: "",
      value: "",
      col: 1,
      style: ''
    },
    {
      title: "Name",
      value: "documentName",
      col: 3,
      style: ''
    },
    {
      title: "Document Type",
      value: "documentType",
      col: 3,
      style: ''
    },
    {
      title: "Uploaded By",
      value: "uploadedBy",
      col: 3,
      style: ''
    },
    {
      title: "Date",
      value: "uploadedDate",
      col: 2,
      style: ''
    },
  ];

  const createValidation = () => {
    let val = Yup.object().shape({
      expirationDate: Yup.string().required("* Required field")
    });

    return val;
  }

  const goToLoginPage = () => {
    history.push('/login');
  };

  const getFiles = (filterQueryString = '', sortBy, callback) => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);

    fitinsur.requestGetDealDocumentsV2(idOperation, filterQueryString, sortBy?.key ? sortBy : null)
      .then(response => response.json()).then(data => {
        if (data.ret_code != 200) {
          mc.error(data.ret_msg_details);
          return;
        }

        setSupportDocuments(data.ret_value.supportDocuments); 
        loading.stop();

        if (callback) callback();
      })
  }

  function getHash() {
    loading.start();
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGetHash(idOperation)
      .then(response => response.json())
      .then(data => {
        if (data.ret_code != 200) {
          mc.error(data.ret_msg_details);
          return;
        }

        if(data.ret_value.metadataReport) {
          setHasHash(true);
          formikRef.current.values.expirationDate = data.ret_value.metadataReport.expirationDate;
          setSelectedItems(data.ret_value.metadataReport.documents);
          setCurrentHash(data.ret_value.idReportSharedDocuments);
        }

      }).catch((error) => {
        loading.stop();
        mc.error(`There was an error while fetching your data: ${getHash.name}\n` + error);
        console.error('fitPostDocument - Error:', error);
      });
  };

  const handleSendHash = values => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);

    const { expirationDate } = values;
    const listDocuments = selectedItems;

    fitinsur.requestCreateHash(listDocuments, expirationDate, idOperation)
      .then(response => response.json())
      .then(data => {
        if (data.ret_code != 200) {
          loading.stop();
          mc.error(data.ret_msg_details);
          return;
        }

        const editUrl = data.ret_value.lnkAccess;
        changeInitialMessage(editUrl);
        loading.stop();

        changeShareDocumentsState(!show);
        changeState(!screenState);
      }).catch((error) => {
        loading.stop();
        mc.error(`There was an error while fetching your data: ${handleSendHash.name}\n` + error);
        console.error('fitPostDocument - Error:', error);
      });
  }


  const handleExpireHash = () => {
    loading.start();
    fitinsur.setAuthToken(authContext.token);

    fitinsur.requestExpireHash(idOperation, currentHash)
      .then(response => response.json())
      .then(data => {
        if (data.ret_code != 200) {
          loading.stop();
          mc.error(data.ret_msg_details);
          return;
        }
        
        changeShareDocumentsState(!show);
        changeState(!screenState);
        loading.stop();
      }).catch((error) => {
        loading.stop();
        mc.error(`There was an error while fetching your data: ${handleExpireHash.name}\n` + error);
        console.error('fitPostDocument - Error:', error);
      });
  }

  function handleSortList(key) {
    let direction = 'asc';
    if (sortBy.key === key && sortBy.direction === 'asc') {
      direction = 'desc';
    };
    const sort = { key: key, direction: direction }
    setSortBy(sort);
    getFiles(location.search, sort);
  }

  useEffect(() => {
    if (authContext.status !== true) {
      mc.error("User is not authenticated, please Login");
      loading.stop();
      goToLoginPage();
      return;
    } else {
      fitinsur.setAuthToken(authContext.token);
      loading.stop();
    };

    getHash();
    getFiles();
  }, []);

  function handleSelect(e, ids = []) {
    if (ids.length >= 1) {
      setSelectedItems([...selectedItems, ...ids]);
      return
    }

    const alreadySelected = selectedItems.findIndex((item) => item.idDocument === e.idDocument);
 
    if (alreadySelected >= 0) {
      const filteredItems = selectedItems.filter((item) => item.idDocument !== e.idDocument);
      setSelectedItems(filteredItems);
    } else {
      if (e) {
        setSelectedItems([...selectedItems, e]);
      }

    };
  };

  function renderDocuments(title, nameArray, hasUpload, hasDelete) {
    return (
      <>  
        {supportDocuments.length > 0 &&
          <>         
            <Row style={{ marginBottom: '10px' }}>
              <Col md={12}>
                <BoxFolder>
                  <FontAwesomeIcon
                    style={{cursor: 'pointer', paddingLeft: '5px'}}
                    icon={faFolderOpen}
                    size="0.5x"
                    color="#6BCABA"
                  />
                  <Text small style={{paddingLeft: '5px'}}>Support &#40;{0 || supportDocuments.length}&#41;</Text>
                </BoxFolder>
              </Col>
            </Row>
            <Row style={{ margin: '15px 0' }}>
              {columns.map((column, index) =>
                <Col sm={column.col} key={index} className={'d-none d-md-block pl-0 ' + column.style}>
                  <Text onClick={() => handleSortList(column.value)}>
                    {column.title}
                  </Text>
                  {sortBy.key === column.value ? <>
                    {column.title == 'Action' ? null : sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> : <Icon name={"arrowdown"} />}</> : <></>}
                </Col>
              )}
            </Row>
          </>
        }
      
        <Row style={{ marginBottom: '10px' }}>
          <Col md={12}>
            {supportDocuments && supportDocuments.map((document, index) => {
              return (
                <React.Fragment key={index}>
                  <Formik>
                    {({ setFieldValue }) => (
                
                      <FieldArray
                        name="listDetail"
                        render={() =>
                          <span>
                            <Row>
                              <Col md={columns[0].col} className="pl-0">
                                <InlineLabel />
                                <label>
                                  <CheckBox
                                    onChange={() => { handleSelect(document) }}
                                    checked={_.find(selectedItems, {'idDocument': document.idDocument}) ? true : false}
                                  />
                                </label>
                              </Col>

                              <Col md={columns[1].col} className="pl-0">
                                <InlineLabel>
                                  {columns[1].title}
                                </InlineLabel>
                                <Label textColor={Colors.GRAY[0]}>
                                  {document.documentName}
                                </Label>
                              </Col>
                              <Col md={columns[2].col} className="pl-0">
                                <InlineLabel>
                                  {columns[2].title}
                                </InlineLabel>
                                <Label textColor={Colors.GRAY[0]}>
                                  {document.documentType}
                                </Label>
                              </Col>
                              <Col md={columns[3].col} className="pl-0">
                                <InlineLabel>
                                  {columns[3].title}
                                </InlineLabel>
                                <Label textColor={Colors.GRAY[0]}>
                                  {document.uploadedBy}
                                </Label>
                              </Col>
                              <Col md={columns[4].col} className="pl-0">
                                <InlineLabel>
                                  {columns[4].title}
                                </InlineLabel>
                                <Label textColor={Colors.GRAY[0]}>
                                  {document.uploadedDate.replaceAll('-', '/')}
                                </Label>
                              </Col>
                            </Row>
                            <Row>
                              <Col className="pl-2 pr-2">
                                <Line />
                              </Col>
                            </Row>
                          </span>
                        }/>
                    )}
                  </Formik>
                </React.Fragment>
              )
            })}
          </Col>
        </Row>
      </>
    );
  }
  
  return (
    <>
      <Dialog canShow={show}>
        <Formik
         innerRef={formikRef}
         initialValues={{expirationDate: ''}}
         validationSchema={Yup.object().shape({ expirationDate: Yup.string().required("* Required field") })}
        >
          {formikProps => {
             return (
              <Form>
                <Row style={{ margin: '20px 0' }}>
                  <Col md={12}>
                    <Text style={{fontWeight: 'bold'}}>Sharing Options</Text>
                  </Col>
                </Row>
                <Row style={{ margin: '10px 0' }}>
                  <Col md={12}>
                    <Text style={{textTransform: 'unset', fontWeight: 'unset'}}>* Expiration Period</Text>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Field
                      name='expirationDate'
                      placeholder='Expiration Period'
                      component={InputField}
                      rightIcon='calendar'
                      value={formikProps.values.expirationDate}
                      rightIconClick={() => { setShowCalendarExpiration(!showCalendarExpiration) }}
                      validation={false}
                      type="text"
                    />

                  {showCalendarExpiration ?
                    <CalendarContainer>
                      <DayPicker
                        style
                        fixedWeeks
                        onDayClick={(date) => {
                          const selectedDate = new Date(date).toLocaleDateString('en-GB');
                          const currentDate = new Date().toLocaleDateString('en-GB');

                          if(selectedDate < currentDate) {
                            mc.error('The date selected must be greater or equal than today!');
                          } else {
                            formikProps?.setFieldValue('expirationDate', selectedDate);
                            setShowCalendarExpiration(false);
                          }
                        }}
                      />
                    </CalendarContainer>
                    : null}
                  </Col>
                </Row>   
                <Row style={{ margin: '25px 0' }}>
                  <Col md={12}>
                    <Text style={{textTransform: 'unset', fontWeight: 'unset'}}>Select the files you want to share in public mode.</Text>
                  </Col>
                </Row>
                {renderDocuments('Support Documents', 'supportDocuments', false, true)}
                <ButtonContainer>
                  <ButtonContent>
                    <Button
                      secundary
                      type="submit"
                      name="Lost"
                      id="btnLost"
                      onClick={() => {changeShareDocumentsState(!show); changeState(!screenState)}}
                    >
                      BACK
                    </Button>

                      {hasHash && (
                        <Button
                          secundary
                          onClick={() => {handleExpireHash()}}>
                          DISABLE
                        </Button>
                      )}
                    

                    <Button
                      primary
                      onClick={() => 
                        createValidation().validate(formikProps.values).then(() => handleSendHash(formikProps.values)).catch((e) => {
                          console.log('validation error', e)
                          mc.error('Please, select at date')
                        })}>
                      SHARE
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </Form>
            )
          }}
        </Formik>
      </Dialog>
    </>
  );
}
